import { useEffect, useState } from "react";
import { Youtube, Facebook, Instagram, Check, Loader2, Plus, X, Share2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner } from "@/components/shared";

const PLATFORMS = [
  { id: "YouTube", icon: Youtube, color: "#FF0000", desc: "Landscape videos & Shorts" },
  { id: "Facebook Pages", icon: Facebook, color: "#1877F2", desc: "Page posts & Reels" },
  { id: "Instagram", icon: Instagram, color: "#E4405F", desc: "Professional accounts & Reels" },
];

export default function Publishing() {
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/publishing/connections").then(({ data }) => setConns(data)).catch(() => setConns([]));
  useEffect(() => { load(); }, []);

  const connect = async (platform) => {
    setBusy(platform);
    try { await api.post("/publishing/connect", { platform }); toast.success(`${platform} connected`); await load(); }
    catch { toast.error("Connection failed"); } finally { setBusy(null); }
  };
  const disconnect = async (id) => { await api.delete(`/publishing/connections/${id}`); toast.success("Disconnected"); load(); };

  if (!conns) return <Spinner label="Loading Publishing…" />;
  const byPlatform = (p) => conns.find((c) => c.platform === p);

  return (
    <CinematicStage>
      <StageHeader stage="18" room="The Distribution Desk · Publishing" icon={Share2} title="Connect once, publish everywhere"
        subtitle="Secure OAuth connections. Publishing is always optional — you stay in control." />

      <GlassCard className="p-4 mb-6 border-[#FFB300]/25">
        <p className="text-sm text-slate-300">OAuth connections are modeled securely (tokens are encrypted at rest). Add platform API credentials in Settings to enable live publishing.</p>
      </GlassCard>

      <div className="grid sm:grid-cols-3 gap-4">
        {PLATFORMS.map((p) => {
          const conn = byPlatform(p.id);
          return (
            <GlassCard key={p.id} hover className="p-6" data-testid={`publish-platform-${p.id}`}>
              <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: `${p.color}22` }}>
                <p.icon className="w-6 h-6" style={{ color: p.color }} />
              </div>
              <h3 className="font-display font-semibold">{p.id}</h3>
              <p className="text-xs text-slate-500 mt-1 mb-4">{p.desc}</p>
              {conn ? (
                <div className="flex items-center justify-between">
                  <Pill tone="green"><Check className="w-3 h-3" /> Connected</Pill>
                  <button onClick={() => disconnect(conn.id)} data-testid={`disconnect-${p.id}`} className="text-slate-400 hover:text-red-400 p-1"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => connect(p.id)} disabled={busy === p.id} data-testid={`connect-${p.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#5B4DFF]/20 border border-[#5B4DFF]/40 text-white hover:bg-[#5B4DFF]/30 text-sm">
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Connect
                </button>
              )}
            </GlassCard>
          );
        })}
      </div>
    </CinematicStage>
  );
}
